// src/pages/cashier/CashierNewOrder.tsx
import React, { useState } from "react";
import { useCashierStore } from "../../store/cashier.store";
import { useUserStore } from "../../store/user.store";
import { useAuth } from "../../context/AuthProvider";
import CashierOrderItemSelector from "./CashierOrderItemSelector";
import CashierPaymentSection from "./CashierPaymentSection";
import { OrderType } from "../../types";
import toast from "react-hot-toast";

type OrderStep = "customer-details" | "item-selection" | "payment";

interface CustomerDetails {
  phone: string;
  name: string;
  orderType: OrderType;
}

const CashierNewOrder: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const [step, setStep] = useState<OrderStep>("customer-details");
  const [customerDetails, setCustomerDetails] = useState<CustomerDetails>({
    phone: "",
    name: "",
    orderType: "delivery",
  });

  const { createDraft, activeDraftId, pipelineOrders } = useCashierStore();
  const { userProfile } = useUserStore();
  const { user } = useAuth();
  const profile = userProfile || user;

  const currentDraft = activeDraftId
    ? pipelineOrders.find((o) => o.id === activeDraftId)
    : null;

  const handleCreateOrder = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate phone for delivery/dinein
    if (
      (customerDetails.orderType === "delivery" ||
        customerDetails.orderType === "dinein") &&
      !customerDetails.phone.trim()
    ) {
      toast.error("Phone number required for delivery/dine-in");
      return;
    }

    // Create draft
    const draft = createDraft({
      orderType: customerDetails.orderType,
      customerPhone: customerDetails.phone.trim() || null,
      customerName: customerDetails.name.trim() || null,
    });

    if (!draft) {
      toast.error("Max 5 orders in pipeline");
      return;
    }

    toast.success("Order created! Add items now.");
    setStep("item-selection");
  };

  // Customer Details Step
  if (step === "customer-details") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 p-4 md:p-8">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="mb-8 flex items-center justify-between">
            <h1 className="text-3xl font-bold text-white">➕ New Order</h1>
            <button
              onClick={onBack}
              className="text-slate-300 hover:text-white transition-colors"
            >
              ✕
            </button>
          </div>

          {/* Form */}
          <form
            onSubmit={handleCreateOrder}
            className="bg-slate-700 rounded-lg p-8 space-y-6"
          >
            {/* Order Type */}
            <div>
              <label className="block text-white font-semibold mb-3">
                Order Type
              </label>
              <div className="grid grid-cols-3 gap-3">
                {(["takeaway", "delivery", "dinein"] as OrderType[]).map(
                  (type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() =>
                        setCustomerDetails({ ...customerDetails, orderType: type })
                      }
                      className={`p-4 rounded-lg font-semibold transition-all ${
                        customerDetails.orderType === type
                          ? "bg-blue-600 text-white"
                          : "bg-slate-600 text-slate-200 hover:bg-slate-500"
                      }`}
                    >
                      {type === "takeaway" && "🛍️"}
                      {type === "delivery" && "🚚"}
                      {type === "dinein" && "🍽️"}
                      <div className="text-sm mt-1 capitalize">{type}</div>
                    </button>
                  )
                )}
              </div>
            </div>

            {/* Customer Name */}
            <div>
              <label className="block text-white font-semibold mb-2">
                Customer Name (optional)
              </label>
              <input
                type="text"
                value={customerDetails.name}
                onChange={(e) =>
                  setCustomerDetails({ ...customerDetails, name: e.target.value })
                }
                placeholder="e.g., Rajesh"
                className="w-full bg-slate-600 text-white rounded-lg px-4 py-3 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Phone Number */}
            <div>
              <label className="block text-white font-semibold mb-2">
                Phone Number
                {(customerDetails.orderType === "delivery" ||
                  customerDetails.orderType === "dinein") && (
                  <span className="text-red-400">*</span>
                )}
              </label>
              <input
                type="tel"
                value={customerDetails.phone}
                onChange={(e) =>
                  setCustomerDetails({ ...customerDetails, phone: e.target.value })
                }
                placeholder="e.g., 9876543210"
  maxLength={10}
                className="w-full bg-slate-600 text-white rounded-lg px-4 py-3 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-slate-400 mt-1">
                {customerDetails.orderType === "takeaway"
                  ? "Optional for takeaway"
                  : "Required for WhatsApp bill"}
              </p>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 rounded-lg transition-colors"
            >
              Continue to Items
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Item Selection Step
  if (step === "item-selection" && currentDraft) {
    return (
      <CashierOrderItemSelector
        draftId={currentDraft.id}
        onItemsSelected={() => setStep("payment")}
        onBack={() => setStep("customer-details")}
      />
    );
  }

  // Payment Step
  if (step === "payment" && currentDraft && profile) {
    return (
      <CashierPaymentSection
        draftId={currentDraft.id}
        cashierUid={profile.uid}
        cashierPhone={profile.phone}
        onSuccess={onBack}
        onBack={() => setStep("item-selection")}
      />
    );
  }

  return null;
};

export default CashierNewOrder;
